import { useCallback, useState, type FormEvent } from 'react';
import type { CollectorChannel } from '@detect/shared';
import { collectorService, type CollectorView, type CollectorCreated } from '../../services/collectors';
import { useCursorList } from '../../hooks/useCursorList';
import { useTenant } from '../../contexts/TenantContext';
import {
  LoadingSpinner,
  ErrorState,
  EmptyState,
  Modal,
  LoadMoreButton,
  CopyableValue,
} from '../../components/common';
import { formatRelativeTime } from '../../utils/formatters';
import { CHANNEL_EXPLANATION, CHANNEL_LABELS, CHANNEL_OPTIONS } from './channel';

/**
 * Collector fleet (PRD §2.1). Claim codes and tokens are shown ONCE in
 * the response that minted them and are never retrievable again — the
 * UI makes that explicit.
 */
export function CollectorsPage() {
  const { tenantId, hasRole } = useTenant();
  const [showCreate, setShowCreate] = useState(false);
  const [claimResult, setClaimResult] = useState<CollectorCreated | null>(null);
  const [tokenResult, setTokenResult] = useState<string | null>(null);
  const [channelFor, setChannelFor] = useState<CollectorView | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);

  const fetchPage = useCallback(
    (tid: string, startAfter: string | undefined) =>
      collectorService.list(tid, { limit: 50, startAfter }),
    []
  );
  const list = useCursorList<CollectorView>(fetchPage, 'all');

  const rotateClaim = async (id: string) => {
    if (!tenantId) return;
    setActionError(null);
    try {
      setClaimResult(await collectorService.rotateClaim(tenantId, id));
      void list.reload();
    } catch (err) {
      setActionError(err);
    }
  };

  const rotateToken = async (id: string) => {
    if (!tenantId) return;
    if (
      !window.confirm(
        'Rotate this collector token? The running collector loses access until reconfigured.'
      )
    ) {
      return;
    }
    setActionError(null);
    try {
      const result = await collectorService.rotateToken(tenantId, id);
      // The new token appears exactly once, in this response.
      setTokenResult(result.token);
      void list.reload();
    } catch (err) {
      setActionError(err);
    }
  };

  const setStatus = async (id: string, status: 'active' | 'disabled') => {
    if (!tenantId) return;
    setActionError(null);
    try {
      await collectorService.update(tenantId, id, { status });
      void list.reload();
    } catch (err) {
      setActionError(err);
    }
  };

  const remove = async (c: CollectorView) => {
    if (!tenantId) return;
    if (!window.confirm(`Delete collector ${c.name}?`)) return;
    setActionError(null);
    try {
      await collectorService.remove(tenantId, c.id);
      void list.reload();
    } catch (err) {
      setActionError(err);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Collectors</h1>
        {hasRole('admin') && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setShowCreate(true)}
          >
            Add collector
          </button>
        )}
      </div>
      {actionError !== null && <ErrorState error={actionError} />}

      {list.error ? (
        <ErrorState error={list.error} onRetry={() => void list.reload()} />
      ) : list.loading ? (
        <LoadingSpinner />
      ) : list.items.length === 0 ? (
        <EmptyState
          title="No collectors"
          description="Add a collector to start monitoring on-site devices."
        />
      ) : (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Host</th>
                <th>Version</th>
                <th>Release channel</th>
                <th>Last heartbeat</th>
                {hasRole('admin') && <th aria-label="Actions" />}
              </tr>
            </thead>
            <tbody>
              {list.items.map((c) => (
                <tr key={c.id}>
                  <td>{c.name}</td>
                  <td>
                    <span className={`status-badge status-${c.status}`}>{c.status}</span>
                  </td>
                  <td>{c.hostname ?? '—'}</td>
                  <td>{c.version ?? '—'}</td>
                  <td>
                    <span className={`status-badge channel-${c.channel}`}>{CHANNEL_LABELS[c.channel]}</span>
                  </td>
                  <td>
                    {c.lastHeartbeatAt ? formatRelativeTime(c.lastHeartbeatAt) : 'never'}
                  </td>
                  {hasRole('admin') && (
                    <td className="row-actions">
                      <button
                        type="button"
                        className="btn btn-ghost"
                        aria-label={`Change release channel of ${c.name}`}
                        onClick={() => setChannelFor(c)}
                      >
                        Change channel
                      </button>
                      {c.status === 'pending' && (
                        <button
                          type="button"
                          className="btn btn-ghost"
                          onClick={() => void rotateClaim(c.id)}
                        >
                          New claim code
                        </button>
                      )}
                      {(c.status === 'active' || c.status === 'down') && (
                        <>
                          <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() => void rotateToken(c.id)}
                          >
                            Rotate token
                          </button>
                          <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() => void setStatus(c.id, 'disabled')}
                          >
                            Disable
                          </button>
                        </>
                      )}
                      {c.status === 'disabled' && (
                        <button
                          type="button"
                          className="btn btn-ghost"
                          onClick={() => void setStatus(c.id, 'active')}
                        >
                          Enable
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => void remove(c)}
                      >
                        Delete
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <LoadMoreButton
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        loadMoreError={list.loadMoreError}
        onLoadMore={() => void list.loadMore()}
      />

      {showCreate && (
        <CreateCollectorModal
          onClose={() => setShowCreate(false)}
          onCreated={(result) => {
            setShowCreate(false);
            setClaimResult(result);
            void list.reload();
          }}
        />
      )}
      {channelFor && (
        <ChannelModal
          collector={channelFor}
          onClose={() => setChannelFor(null)}
          onSaved={() => {
            setChannelFor(null);
            void list.reload();
          }}
        />
      )}
      {tokenResult && (
        <Modal title="New collector token" onClose={() => setTokenResult(null)}>
          <p>
            Update the collector configuration with this token.{' '}
            <strong>It is shown only once</strong> — the old token stopped working
            the moment it was rotated.
          </p>
          <CopyableValue value={tokenResult} label="Copy collector token" />
        </Modal>
      )}
      {claimResult && (
        <Modal title="Claim code" onClose={() => setClaimResult(null)}>
          <p>
            Run the collector installer with this one-time claim code.{' '}
            <strong>It is shown only once</strong> — it cannot be retrieved later
            (rotate to mint a new one).
          </p>
          <CopyableValue value={claimResult.claimCode} label="Copy claim code" />
          {claimResult.claimExpiresAt && (
            <p className="text-muted">Expires {claimResult.claimExpiresAt}</p>
          )}
        </Modal>
      )}
    </div>
  );
}

function CreateCollectorModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (result: CollectorCreated) => void;
}) {
  const { tenantId } = useTenant();
  const [name, setName] = useState('');
  const [channel, setChannel] = useState<CollectorChannel>('stable');
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!tenantId) return;
    setSubmitting(true);
    setError(null);
    try {
      onCreated(await collectorService.create(tenantId, { name, channel }));
    } catch (err) {
      setError(err);
      setSubmitting(false);
    }
  };

  return (
    <Modal title="Add collector" onClose={onClose}>
      <form onSubmit={submit}>
        {error !== null && <ErrorState error={error} />}
        <div className="form-field">
          <label htmlFor="collector-name">Name</label>
          <input
            id="collector-name"
            name="name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <ChannelSelect id="collector-channel" value={channel} onChange={setChannel} />
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? 'Creating…' : 'Create and get claim code'}
        </button>
      </form>
    </Modal>
  );
}

/** The channel choice, with what each means and the one sentence on what the channel does. */
function ChannelSelect({
  id,
  value,
  onChange,
}: {
  id: string;
  value: CollectorChannel;
  onChange: (c: CollectorChannel) => void;
}) {
  const hint = CHANNEL_OPTIONS.find((o) => o.value === value)?.hint;
  return (
    <div className="form-field">
      <label htmlFor={id}>Release channel</label>
      <select
        id={id}
        name="channel"
        value={value}
        onChange={(e) => onChange(e.target.value as CollectorChannel)}
      >
        {CHANNEL_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <p className="text-muted">{CHANNEL_EXPLANATION}</p>
      {hint && <p className="text-muted">{hint}</p>}
    </div>
  );
}

function ChannelModal({
  collector,
  onClose,
  onSaved,
}: {
  collector: CollectorView;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { tenantId } = useTenant();
  const [channel, setChannel] = useState<CollectorChannel>(collector.channel);
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!tenantId) return;
    setSubmitting(true);
    setError(null);
    try {
      await collectorService.update(tenantId, collector.id, { channel });
      onSaved();
    } catch (err) {
      setError(err);
      setSubmitting(false);
    }
  };

  return (
    <Modal title={`Release channel — ${collector.name}`} onClose={onClose}>
      <form onSubmit={submit}>
        {error !== null && <ErrorState error={error} />}
        <ChannelSelect id="collector-channel-edit" value={channel} onChange={setChannel} />
        <button
          type="submit"
          className="btn btn-primary"
          disabled={submitting || channel === collector.channel}
        >
          {submitting ? 'Saving…' : 'Save'}
        </button>
      </form>
    </Modal>
  );
}
